import React, { useEffect, useState } from 'react';

import { Controller, useForm } from 'react-hook-form';
import { Prompt } from 'react-router';
import { useLocation } from 'react-router-dom';
import { TemplateType } from 'types/narrative/narrative.types';

import { DEFAULT_ERROR, useCustomSnackBar } from 'hooks/useCustomSnackBar';
import { useQueryState } from 'hooks/useQueryState';
import useUserHook from 'hooks/useUserHook';

import { QueryParams as TestNarrativeTextQueryParams } from 'api/mutations/testNarrativeText.mutation';
import { QueryParams as UpdateTemplateTextQueryParams } from 'api/mutations/updateTemplateText.mutation';

import { MuiIcon } from 'components/common/Icons/base/MuiIcon/MuiIcon';
import { WidgetWithTitle } from 'components/common/WidgetWithTitle/WidgetWithTitle';

import { Button } from 'components/DesignSystem/Button';
import { Radio } from 'components/DesignSystem/Radio';
import { Select } from 'components/DesignSystem/Select';
import { Switch } from 'components/DesignSystem/Switch';
import { NarrativeTextEditor } from 'components/Settings/Components/NarrativeTemplates/components/NarrativeTemplates/NarrativeTextEditor/narrativeTextEditor';
import { useComponentListOptions } from 'components/Settings/Components/NarrativeTemplates/components/NarrativeTemplates/_hooks/useComponentListOptions';
import { useNarrativeTemplatesProvider } from 'components/Settings/Components/NarrativeTemplates/components/NarrativeTemplates/_hooks/useNarrativeTemplatesProvider';
import classes from 'components/Settings/Components/NarrativeTemplates/components/NarrativeTemplates/narrativeTemplates.module.scss';
import { CustomizedFormErrorMessage, CustomizedTooltip } from 'components/common';

export interface NarrativeTemplateForm {
    component: ISelectOption<string> | undefined;
    narrative: string;
    aiNarrative: string;
    applyUpdateToAllCustomers: boolean;
    templateType: TemplateType;
}

const TEST_NARRATIVE_ERROR_MESSAGE = 'Error running test template.';

const TemplateTypes = {
    BASIC: 'BASIC',
    PROMPT: 'PROMPT',
} as const;

export const NarrativeTemplates = () => {
    const {
        formState: { isDirty, errors, isSubmitted },
        control,
        handleSubmit,
        getValues,
        watch,
        setValue,
        resetField,
    } = useForm<NarrativeTemplateForm>({
        defaultValues: {
            applyUpdateToAllCustomers: false,
            templateType: TemplateTypes.BASIC,
        },
    });
    const narrativeValueFromForm = watch('narrative');
    const aiNarrativeValueFromForm = watch('aiNarrative');
    const componentFromForm = watch('component');
    const selectedTemplateTypeFromForm = watch('templateType');

    //API
    const { queryData, queryMutations, queryUtils } = useNarrativeTemplatesProvider(componentFromForm);
    const { contract, project, latestVersion, baselineVersion } = queryData.project;

    //Hooks
    const [componentKeyUrlParam, setComponentKeyUrlParam] = useQueryState('componentKey');
    const { handleSnackBar } = useCustomSnackBar();
    const { ability } = useUserHook();
    const currentLocation = useLocation();
    const componentListOptions = useComponentListOptions(queryData.componentList);

    const isAdmin = ability.can('all', 'Admin');
    const isAbleToEditNarrativeTemplatePrompt = ability.can('view', 'EditNarrativeTemplatePrompt');
    const isBasic = selectedTemplateTypeFromForm === TemplateTypes.BASIC;
    const isPrompt = selectedTemplateTypeFromForm === TemplateTypes.PROMPT;

    const [testValue, setTestValue] = useState<string>(queryMutations.testNarrativeText.data ?? ' ');

    // callbacks
    const syncNarrativeValueToApiResponseOrEmpty = () => {
        let aiNarrative = '';
        let basicNarrative = '';

        if (queryData.narrativeTemplatePerComponent?.aiText) {
            aiNarrative = queryData.narrativeTemplatePerComponent.aiText;
        }
        if (queryData.narrativeTemplatePerComponent?.text) {
            basicNarrative = queryData.narrativeTemplatePerComponent.text;
        }
        setValue('aiNarrative', aiNarrative);
        setValue('narrative', basicNarrative);
    };

    useEffect(() => {
        // Update form values with data from backend- when data arrives or new component selected
        syncNarrativeValueToApiResponseOrEmpty();
    }, [
        queryData.narrativeTemplatePerComponent?.text,
        queryData.narrativeTemplatePerComponent?.aiText,
        componentFromForm,
    ]);

    useEffect(() => {
        //sync form radio button to backend saved
        let templateType: TemplateType = TemplateTypes.BASIC;
        if (queryData.narrativeTemplatePerComponent?.templateType) {
            templateType = queryData.narrativeTemplatePerComponent?.templateType;
        }
        setValue('templateType', templateType);
    }, [queryData.narrativeTemplatePerComponent?.templateType]);

    useEffect(() => {
        //Select component from components dropdown
        if (!componentFromForm && componentListOptions && componentListOptions.length > 0) {
            if (componentKeyUrlParam) {
                const selectedComponent = componentListOptions.find((item) => item.value === componentKeyUrlParam);
                setValue('component', selectedComponent);
            } else {
                // select first component and sync url param
                const firstComponent = componentListOptions[0];
                setComponentKeyUrlParam(firstComponent.value);
                setValue('component', firstComponent);
            }
        }
    }, [componentListOptions, componentFromForm]);

    const onRunTestTemplate = () => {
        const formValues = getValues();
        const selectedTemplateType = formValues.templateType;

        let narrativeTextForTest = formValues.narrative;
        if (isPrompt) {
            narrativeTextForTest = formValues.aiNarrative;
        }

        if (!latestVersion && !baselineVersion) {
            handleSnackBar(
                'The test operation cannot be performed because the current project has no versions or only a single version available.',
                'error',
            );
        }

        if (baselineVersion && baselineVersion.id && latestVersion && latestVersion.id && componentFromForm) {
            const payload: TestNarrativeTextQueryParams = {
                compareMetaDataId: baselineVersion.id,
                actualMetaDataId: latestVersion.id,
                narrativeText: narrativeTextForTest,
                templateType: selectedTemplateType,
                filter: {
                    filters: undefined,
                },
                componentKey: componentFromForm?.value,
            };
            queryMutations.testNarrativeText.mutate(payload, {
                onSuccess: (value) => {
                    setTestValue(value);
                },
                onError: (error) => {
                    setTestValue(TEST_NARRATIVE_ERROR_MESSAGE);
                    DEFAULT_ERROR({ error, handleError: handleSnackBar });
                },
            });
        }
    };

    const onSubmit = (data: NarrativeTemplateForm) => {
        const { component, narrative, aiNarrative, applyUpdateToAllCustomers, templateType } = data;

        if (!component?.value || component.id == null || isNaN(Number(component.id))) {
            return;
        }

        const payload: UpdateTemplateTextQueryParams = {
            componentKey: component.value,
            componentId: Number(component.id),
            text: narrative,
            aiText: aiNarrative,
            forCurrentCustomerOnly: !applyUpdateToAllCustomers,
            templateType,
        };

        queryMutations.updateTemplateText.mutate(payload, {
            onSuccess: () => {
                handleSnackBar('Successfully saved!', 'success');
                resetField('applyUpdateToAllCustomers');
            },
            onError: (error) => {
                DEFAULT_ERROR({ error, handleError: handleSnackBar });
            },
            onSettled: () => {
                queryUtils.queryClient.invalidateQueries('getTemplateByKey');
            },
        });
    };

    return (
        <WidgetWithTitle
            id={'narrativeTemplatesForm_FormTitle'}
            title={'Edit Templates'}
            className={classes.narrativeTemplatesContainer}
        >
            <form className={classes.narrativesTemplatesForm} onSubmit={handleSubmit(onSubmit)}>
                <div className={classes.componentSelectSection}>
                    <div className={classes.labelContainer}>
                        <label>Select Component:</label>
                    </div>
                    <Controller
                        render={({ field }) => (
                            <Select<ISelectOption<string>>
                                {...field}
                                onChange={(value) => {
                                    setTestValue(' ');
                                    field.onChange(value);
                                    setComponentKeyUrlParam(value?.value);
                                }}
                                options={componentListOptions}
                                isError={!!errors.component}
                            />
                        )}
                        name={'component'}
                        control={control}
                        rules={{ required: 'Please select component' }}
                    />
                    {errors.component && <CustomizedFormErrorMessage text={errors.component.message} />}
                </div>

                <div className={classes.componentNarrativeTemplateSection}>
                    <div className={classes.componentNarrativeTemplateRow}>
                        <div className={classes.labelContainer}>
                            <label>Component Narrative Template:</label>
                            <CustomizedTooltip
                                tooltipContent={
                                    <div className={classes.content}>
                                        <div>
                                            <span className={classes.bold}>Deterministic</span>: Compose a narrative
                                            template based on deterministic simple text, and optionally embedding
                                            parameter values from the component results.
                                        </div>
                                        <div>
                                            <span className={classes.bold}>Generative</span>: Generate narrative
                                            template by providing an AI Prompt, and optionally embedding parameter
                                            values from the component results.
                                        </div>
                                        <div className={classes.tip}>
                                            Tip: Type [ (square bracket) to open list of available component parameters.
                                        </div>
                                    </div>
                                }
                                triggerElement={
                                    <MuiIcon
                                        icon={'info'}
                                        color={'var(--text-brand-primary)'}
                                        fontSize={'24px'}
                                        type={'outlined'}
                                    />
                                }
                            />
                        </div>
                        <div className={classes.radioButtonsContainer}>
                            <div className={classes.radioButtonsWrapper}>
                                <Controller
                                    render={({ field }) => (
                                        <Radio
                                            {...field}
                                            value={TemplateTypes.BASIC}
                                            name={'basic-option'}
                                            label={'Deterministic'}
                                            checked={selectedTemplateTypeFromForm === TemplateTypes.BASIC}
                                            onChange={(e) => {
                                                field.onChange(e.target.value);
                                            }}
                                        />
                                    )}
                                    name={'templateType'}
                                    control={control}
                                />
                                <Controller
                                    render={({ field }) => (
                                        <Radio
                                            {...field}
                                            value={TemplateTypes.PROMPT}
                                            checked={selectedTemplateTypeFromForm === TemplateTypes.PROMPT}
                                            name={'prompt-option'}
                                            disabled={!isAbleToEditNarrativeTemplatePrompt}
                                            label={'Generative'}
                                            onChange={(e) => {
                                                field.onChange(e.target.value);
                                            }}
                                        />
                                    )}
                                    name={'templateType'}
                                    control={control}
                                />
                            </div>
                        </div>

                        <div className={classes.buttonsWrapper}>
                            {isAdmin && (
                                <div>
                                    <Controller
                                        render={({ field }) => (
                                            <Switch
                                                onChange={field.onChange}
                                                checked={field.value}
                                                label={'Apply update to all customers'}
                                                labelPlacement={'end'}
                                            />
                                        )}
                                        name={'applyUpdateToAllCustomers'}
                                        control={control}
                                    />
                                </div>
                            )}
                            <Button type={'submit'} isLoading={queryMutations.updateTemplateText.isLoading}>
                                Save
                            </Button>

                            <Button variant={'secondary'} onClick={() => syncNarrativeValueToApiResponseOrEmpty()}>
                                Revert Changes
                            </Button>
                        </div>
                    </div>
                    {errors.narrative && (
                        <div className={classes.errorMessage}>
                            <CustomizedFormErrorMessage text={errors.narrative.message} />
                        </div>
                    )}
                    <div style={{ display: isBasic ? 'block' : 'none' }}>
                        <Controller
                            render={({ field }) => (
                                <NarrativeTextEditor
                                    placeholder={
                                        'Enter template here. Type [ (square bracket) to open list of available component parameters.'
                                    }
                                    value={field.value}
                                    onChange={field.onChange}
                                    isError={!!errors.narrative}
                                    disabled={componentFromForm === null || componentFromForm === undefined}
                                    templatePlaceholders={queryData.narrativeTemplatePerComponent?.placeholders}
                                />
                            )}
                            name={'narrative'}
                            control={control}
                            rules={{
                                validate: (v) =>
                                    isBasic ? !!v?.trim() || 'Narrative template must not be empty' : true,
                            }}
                        />
                    </div>
                    <div style={{ display: isPrompt ? 'block' : 'none' }}>
                        <Controller
                            render={({ field }) => (
                                <NarrativeTextEditor
                                    placeholder={
                                        'Enter template here. Type [ (square bracket) to open list of available component parameters.'
                                    }
                                    value={field.value}
                                    onChange={field.onChange}
                                    isError={!!errors.aiNarrative}
                                    disabled={componentFromForm === null || componentFromForm === undefined}
                                    templatePlaceholders={queryData.narrativeTemplatePerComponent?.placeholders}
                                />
                            )}
                            name={'aiNarrative'}
                            control={control}
                            rules={{
                                validate: (v) =>
                                    isPrompt ? !!v?.trim() || 'Narrative template must not be empty' : true,
                            }}
                        />
                    </div>
                </div>

                <div className={classes.testConfigurationWrapper}>
                    <div className={classes.labelContainer}>
                        <label>Test Configuration:</label>
                    </div>
                    <div className={classes.testWrapper}>
                        <div className={classes.testProjectTitle}>
                            <Button
                                size={'medium'}
                                variant={'secondary'}
                                disabled={
                                    (isBasic && !narrativeValueFromForm?.length) ||
                                    (isPrompt && !aiNarrativeValueFromForm?.length) ||
                                    queryMutations.testNarrativeText.isLoading
                                }
                                isLoading={queryMutations.testNarrativeText.isLoading}
                                onClick={onRunTestTemplate}
                            >
                                Test Template
                            </Button>
                            <div className={classes.versionWrapper}>
                                <label className={classes.fieldName}>Test Project:</label>
                                <span className={classes.versionValue}>
                                    {project?.title} / {contract?.title}
                                </span>
                            </div>
                            {latestVersion && (
                                <div className={classes.versionWrapper}>
                                    <label className={classes.fieldName}>Version:</label>
                                    <span className={classes.versionValue}>{latestVersion.versionName} | Latest</span>
                                </div>
                            )}
                            {baselineVersion && (
                                <div className={classes.versionWrapper}>
                                    <label className={classes.fieldName}>Compare to:</label>
                                    <span className={classes.versionValue}>
                                        {baselineVersion.versionName} | Baseline
                                    </span>
                                </div>
                            )}
                        </div>
                        <div
                            className={classes.testPreview}
                            dangerouslySetInnerHTML={{
                                __html: testValue,
                            }}
                        />
                    </div>
                </div>
            </form>

            <Prompt
                when={isDirty && !isSubmitted}
                message={(location) => {
                    const isOnlyQueryChanged = location.pathname === currentLocation.pathname;

                    if (isOnlyQueryChanged) return true; // allow silently
                    return 'You have unsaved changes. Are you sure you want to leave?';
                }}
            />
        </WidgetWithTitle>
    );
};
